// AGC: UsageDashboard - Display current usage vs limits
// AGC: Shows progress bars for each limited feature

import React from 'react'
import { 
  CheckSquare, 
  Map, 
  Mic, 
  MessageCircle, 
  Ruler, 
  Users, 
  Calculator,
  TrendingUp,
  Calendar,
  AlertTriangle,
} from 'lucide-react'
import { useSubscription } from '../../context/SubscriptionContext'
import { useTranslation } from '../../hooks/useTranslation'
import { useActiveTeamMemberCount } from '../../hooks/useActiveTeamMemberCount'
import type { LimitedFeature } from '../../types/subscription'

interface UsageDashboardProps {
  compact?: boolean
  showTitle?: boolean
  features?: LimitedFeature[]
}

/**
 * AGC: Feature info for display
 */
const FEATURE_INFO: Record<LimitedFeature, {
  icon: React.ReactNode
  labelKey: string
  color: string
  bgColor: string
}> = {
  tasks: {
    icon: <CheckSquare className="w-4 h-4" />,
    labelKey: 'subscription.usage.tasks',
    color: 'text-blue-600',
    bgColor: 'bg-blue-500',
  },
  fields: {
    icon: <Map className="w-4 h-4" />,
    labelKey: 'subscription.usage.fields',
    color: 'text-green-600',
    bgColor: 'bg-green-500',
  },
  voiceCommands: {
    icon: <Mic className="w-4 h-4" />,
    labelKey: 'subscription.usage.voiceCommands',
    color: 'text-purple-600',
    bgColor: 'bg-purple-500',
  },
  whatsAppAudio: {
    icon: <MessageCircle className="w-4 h-4" />,
    labelKey: 'subscription.usage.whatsAppAudio',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-500',
  },
  hectares: {
    icon: <Ruler className="w-4 h-4" />,
    labelKey: 'subscription.usage.hectares',
    color: 'text-amber-600',
    bgColor: 'bg-amber-500',
  },
  teamMembers: {
    icon: <Users className="w-4 h-4" />,
    labelKey: 'subscription.usage.teamMembers',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-500',
  },
  hectareCalculator: {
    icon: <Calculator className="w-4 h-4" />,
    labelKey: 'subscription.usage.hectareCalculator',
    color: 'text-rose-600',
    bgColor: 'bg-rose-500',
  },
}

/**
 * AGC: Progress bar component
 */
const ProgressBar: React.FC<{
  percentage: number
  bgColor: string
  isUnlimited?: boolean
}> = ({ percentage, bgColor, isUnlimited }) => {
  // AGC: Determine color based on percentage
  const getBarColor = () => {
    if (isUnlimited) return 'bg-gray-300 dark:bg-slate-600'
    if (percentage >= 90) return 'bg-red-500'
    if (percentage >= 70) return 'bg-yellow-500'
    return bgColor
  }

  return (
    <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
      <div
        className={`h-full ${getBarColor()} transition-all duration-300`}
        style={{ width: isUnlimited ? '0%' : `${Math.min(100, percentage)}%` }}
      />
    </div>
  )
}

/**
 * AGC: Single usage item component
 */
const UsageItem: React.FC<{
  feature: LimitedFeature
  current: number
  limit: number | null
  percentage: number
  compact?: boolean
}> = ({ feature, current, limit, percentage, compact }) => {
  const { t } = useTranslation()
  const info = FEATURE_INFO[feature]
  const isUnlimited = limit === null
  const isWarning = percentage >= 70 && percentage < 90
  const isCritical = percentage >= 90

  // AGC: Fallback labels if translations not loaded
  const featureLabels: Record<LimitedFeature, string> = {
    tasks: 'Tarefas',
    fields: 'Talhões',
    voiceCommands: 'Comandos de Voz',
    whatsAppAudio: 'Áudios WhatsApp',
    hectares: 'Hectares',
    teamMembers: 'Membros da Equipe',
    hectareCalculator: 'Calculadora ha',
  }

  const label = t(info.labelKey) || featureLabels[feature]

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <span className={info.color}>{info.icon}</span>
        <span className="text-xs text-gray-600 dark:text-gray-400 flex-1 truncate">
          {label}
        </span>
        <span className={`text-xs font-medium ${
          isCritical ? 'text-red-600' : isWarning ? 'text-yellow-600' : 'text-gray-700 dark:text-gray-300'
        }`}>
          {isUnlimited ? '∞' : `${current}/${limit}`}
        </span>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={info.color}>{info.icon}</span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {label}
          </span>
          {isCritical && (
            <AlertTriangle className="w-4 h-4 text-red-500" />
          )}
        </div>
        <span className={`text-sm font-semibold ${
          isCritical ? 'text-red-600' : isWarning ? 'text-yellow-600' : 'text-gray-700 dark:text-gray-300'
        }`}>
          {isUnlimited ? (
            <span className="text-emerald-600">∞ {t('subscription.unlimited') || 'Ilimitado'}</span>
          ) : (
            `${current} / ${limit}`
          )}
        </span>
      </div>
      <ProgressBar 
        percentage={percentage} 
        bgColor={info.bgColor} 
        isUnlimited={isUnlimited}
      />
      {!isUnlimited && percentage >= 80 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {percentage >= 100 
            ? (t('subscription.limitReached') || 'Limite atingido')
            : `${100 - percentage}% ${t('subscription.remaining') || 'restante'}`
          }
        </p>
      )}
    </div>
  )
}

/**
 * AGC: Usage Dashboard Component
 */
export const UsageDashboard: React.FC<UsageDashboardProps> = ({
  compact = false,
  showTitle = true,
  features,
}) => {
  const { t } = useTranslation()
  const { checkAllFeatureLimits, daysUntilReset, resetDateString, tier } = useSubscription()
  const { count: activeTeamCount } = useActiveTeamMemberCount()

  const rawLimits = checkAllFeatureLimits()
  // AGC: Override teamMembers with the live count from fields/{farmId}/members (the usage doc counter drifts).
  const teamLimit = rawLimits.teamMembers.limit
  const teamPercentage = teamLimit === null || teamLimit === 0
    ? 0
    : Math.round((activeTeamCount / teamLimit) * 100)
  const allLimits = {
    ...rawLimits,
    teamMembers: {
      ...rawLimits.teamMembers,
      current: activeTeamCount,
      percentage: teamPercentage,
      allowed: teamLimit === null || activeTeamCount < teamLimit,
    },
  }
  
  // AGC: Features to display (default: all)
  const displayFeatures: LimitedFeature[] = features || [
    'tasks',
    'fields',
    'voiceCommands',
    'whatsAppAudio',
    'hectares',
    'teamMembers',
    'hectareCalculator',
  ]

  // AGC: Filter out features with 0 limit (not available in tier)
  const availableFeatures = displayFeatures.filter(f => {
    const limit = allLimits[f]
    // AGC: Show if unlimited or has positive limit
    return limit.limit === null || limit.limit > 0
  })

  if (compact) {
    return (
      <div className="space-y-2">
        {availableFeatures.slice(0, 4).map(feature => (
          <UsageItem
            key={feature}
            feature={feature}
            current={allLimits[feature].current}
            limit={allLimits[feature].limit}
            percentage={allLimits[feature].percentage}
            compact
          />
        ))}
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700">
      {showTitle && (
        <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('subscription.usage.title') || 'Uso do Mês'}
              </h3>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Calendar className="w-4 h-4" />
              <span>
                {t('subscription.usage.resetsIn') || 'Renova em'} {daysUntilReset} {t('subscription.usage.days') || 'dias'}
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('subscription.usage.resetDate') || 'Próxima renovação:'} {resetDateString}
          </p>
        </div>
      )}
      
      <div className="p-4 space-y-4">
        {availableFeatures.map(feature => (
          <UsageItem
            key={feature}
            feature={feature}
            current={allLimits[feature].current}
            limit={allLimits[feature].limit}
            percentage={allLimits[feature].percentage}
          />
        ))}
        
        {tier === 'premium' && (
          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <p className="text-sm text-yellow-700 dark:text-yellow-300 flex items-center gap-2">
              <span className="text-lg">👑</span>
              {t('subscription.premiumUnlimited') || 'Plano Premium: Todos os recursos ilimitados!'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default UsageDashboard





